<!--
// app/pages/[region]/stats/Cards.vue
-->
<script setup lang="ts">
import type { GroupedRanking } from "~/src/stats/domain/models/Rankings";
import { useSeasonFilter } from "~/app/composables/season/seasonFilter";
import { useWebContext } from "~/app/composables/webContext";
import statsService from "~/src/stats/domain/services/statsService";
import SeasonsFilter from "~/app/components/Shared/SeasonsFilter.vue";
import HeaderBannerTitle from "~/app/components/Shared/HeaderBannerTitle.vue";
import SidebarMenuStats from "~/app/components/pages/region-stats/SidebarMenuStats.vue";
import BaseBackdropLoading from "~/app/components/base/BaseBackdropLoading.vue";
import StatsDataTable from "~/app/components/pages/region-stats/StatsDataTable.vue";
import { $SEASONS_WITHOUT_DATA } from "~/src/season-cluster/infrastructure/ui-services/seasonUiService";
import ButtonSelectTypeStats from "~/app/components/pages/region-stats/ButtonSelectTypeStats.vue";

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: "/[region]/stats/cards",
    es: "/[region]/estadisticas/tarjetas",
    de: "/[region]/statistiken/karten",
    fr: "/[region]/statistiques/cartons",
    it: "/[region]/statistiche/cartellini",
    pt: "/[region]/estatisticas/cartoes",
    ar: "/[region]/stats/cards",
  },
});

definePageMeta({
  middleware: ["not-found-king-of-kings-nations"],
});

const { seasonId, seasonClusterId } = useSeasonFilter();
const { mergeRankingsByPlayerId } = statsService();
const { webContextGenders } = useWebContext();
const { t } = useI18n();
const tableStatsKey = ref<number>(0);
const sidebarKey = ref<number>(0);
const loadingSeasonData = ref<boolean>(false);

const errorMessage = ref<boolean>(false);
const tableDataPlayers = ref<GroupedRanking[] | undefined>([]);

const mainStat = ref<string>("CRT-G");
const otherStats = ref<string>("CRT-R,PG");
const allStatsPlayer: string[] = ["CRT-G", "CRT-R", "PG"];
const fallbackMainStat: string = "CRT-G";

const getSeasonData = async (): Promise<void> => {
  try {
    loadingSeasonData.value = true;
    errorMessage.value = false;

    const seasonIdValue = seasonId.value;

    const { data } = await useFetch("/api/stats", {
      query: {
        season: seasonIdValue,
        type: "players",
        mainCode: mainStat.value,
        secondaryCodes: otherStats.value,
        orderBy: "ranking",
        sortOrder: "ASC",
        ctxGenders: webContextGenders,
      },
    });
    tableDataPlayers.value = data.value || [];
  } catch (e) {
    errorMessage.value = true;
  } finally {
    loadingSeasonData.value = false;
    tableStatsKey.value++;
    sidebarKey.value++;
  }
};

await nextTick();
await getSeasonData();

useSeoMeta({
  title: t("menuItem.stats") + " " + t("menuItem.statsPlayers") + " - " + t("stats.sidebar.menu.Cards"),
  ogTitle: t("menuItem.stats") + " " + t("menuItem.statsPlayers") + " - " + t("stats.sidebar.menu.Cards"),
});

const { sortStatsTable } = statsService();

const executeSortPlayers = async sortValue => {
  const codeSortValue = sortValue.sortField.split(".")[1];
  const sortOrderValue = sortValue.sortOrder;

  mainStat.value = codeSortValue;
  tableDataPlayers.value = await sortStatsTable(
    codeSortValue,
    allStatsPlayer,
    fallbackMainStat,
    seasonId.value,
    "players",
    sortOrderValue
  );
};

const dataFieldsPlayers = computed(() => {
  return {
    ranking: `rankingData.${mainStat.value}.ranking`,
    name: "player",
    sortField: "rankingData.CRT-G.total",
    stats: [
      { stat: "rankingData.PG.total", header: t("PJ"), sortable: false, tooltip: t("tooltip.PJ") },
      { stat: "rankingData.CRT-G.total", header: t("T.A."), sortable: true, tooltip: t("tooltip.T.A.") },
      { stat: "rankingData.CRT-R.total", header: t("T.R."), sortable: true, tooltip: t("tooltip.T.R.") },
    ],
  };
});
</script>

<template>
  <header-banner-title
    :title="$t('general.stats.title') + ' ' + $t('menuItem.statsPlayers')"
  ></header-banner-title>
  <section class="px-4 md:px-6">
    <div
      class="kql-w-container flex flex-col md:flex-row items-stretch sm:items-end justify-center md:items-center md:justify-between flex-wrap"
    >
      <button-select-type-stats
        v-if="!$SEASONS_WITHOUT_DATA.includes(Number(seasonId))"
        data-type="players"
        page="Cards"
      ></button-select-type-stats>
      <div v-else></div>
      <seasons-filter
        v-model:seasonClusterId="seasonClusterId"
        v-model:seasonId="seasonId"
        @change="getSeasonData"
        combined-with-more-filters
      />
    </div>
  </section>

  <section class="px-4 md:px-6 my-9">
    <div class="kql-w-container grid grid-cols-1 lg:grid-cols-4 py-9 px-4 bg-gray-900 rounded-lg lg:gap-4">
      <div class="mb-9 lg:mb-0 relative">
        <sidebar-menu-stats
          :new-stats="!$SEASONS_WITHOUT_DATA.includes(Number(seasonId))"
          :key="sidebarKey"
          :season-id="seasonId"
        ></sidebar-menu-stats>
      </div>
      <div class="col-span-3 relative">
        <base-backdrop-loading :active="loadingSeasonData"></base-backdrop-loading>

        <div v-if="errorMessage">
          {{ $t("stats.message.notAvailable") }}
        </div>

        <div v-else class="stats-table-data max-w-full overflow-hidden" :key="tableStatsKey">
          <stats-data-table
            :main-stat="mainStat"
            :table-data="tableDataPlayers"
            :data-fields="dataFieldsPlayers"
            @sort="e => executeSortPlayers(e)"
          ></stats-data-table>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped></style>
